import { useLocation } from 'react-router-dom';
import type { Language } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { IconBell, IconMoon, IconSun } from '../icons';

interface TopBarProps {
  lang: Language;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onToggleLang: () => void;
  isMobile?: boolean;
  onOpenMenu?: () => void;
}

const ROUTE_TITLES: Record<string, ((nav: Record<string, string>) => string) | undefined> = {
  '/dashboard': (n) => n['dashboard'] ?? '',
  '/chat': (n) => n['newSearch'] ?? '',
  '/results': (n) => n['results'] ?? '',
  '/favorites': (n) => n['favorites'] ?? '',
  '/settings': (n) => n['settings'] ?? '',
};

const S = {
  topbar: {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    backgroundColor: 'var(--bg-base)',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  hamburger: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 16,
    flexShrink: 0,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  breadcrumbApp: {
    fontSize: 13,
    color: 'var(--text-muted)',
    fontWeight: 500,
  },
  breadcrumbSep: {
    fontSize: 13,
    color: 'var(--border-strong)',
  },
  breadcrumbPage: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
  },
  langBtn: {
    height: 34,
    padding: '0 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.5px',
    transition: 'background 0.12s, color 0.12s',
  },
};

export function TopBar({
  lang,
  theme,
  onToggleTheme,
  onToggleLang,
  isMobile = false,
  onOpenMenu,
}: TopBarProps) {
  const location = useLocation();
  const nav = I18N[lang].nav;

  // Find best matching route title
  const matchKey = Object.keys(ROUTE_TITLES).find((k) =>
    location.pathname === k || location.pathname.startsWith(k + '/'),
  );
  const pageTitle = matchKey
    ? (ROUTE_TITLES[matchKey]?.(nav as Record<string, string>) ?? 'Sourcr')
    : 'Sourcr';

  return (
    <header style={S.topbar}>
      {/* Left: hamburger (mobile) + breadcrumb */}
      <div style={S.left}>
        {isMobile && (
          <button
            type="button"
            onClick={onOpenMenu}
            style={S.hamburger}
            aria-label="Open navigation menu"
            title="Open menu"
          >
            ☰
          </button>
        )}
        <div style={S.breadcrumb}>
          {!isMobile && (
            <>
              <span style={S.breadcrumbApp}>Sourcr</span>
              <span style={S.breadcrumbSep}>/</span>
            </>
          )}
          <span style={S.breadcrumbPage}>{pageTitle}</span>
        </div>
      </div>

      {/* Actions */}
      <div style={S.actions}>
        {/* Language toggle */}
        <button
          type="button"
          onClick={onToggleLang}
          style={S.langBtn}
          title="Toggle language"
          aria-label={`Switch to ${lang === 'en' ? 'Ukrainian' : 'English'}`}
        >
          {lang === 'en' ? 'EN' : 'УК'}
        </button>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={onToggleTheme}
          style={S.iconBtn}
          title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
        </button>

        {/* Notifications — hidden on mobile to save space */}
        {!isMobile && (
          <button
            type="button"
            style={S.iconBtn}
            title="Notifications"
            aria-label="View notifications"
          >
            <IconBell size={15} />
          </button>
        )}
      </div>
    </header>
  );
}
